/**
 * @description List of possible date formats for a given set of date strings assumin - is the separator
 * @param {string[]} dateStrings List of date strings
 * @returns {string[]} List of possible date formats
 */
export const possibleDateFormats = (dateStrings: string[]): string[] => {
    let posbleFormatsList = [
        "yyyy-mm-dd",
        "yyyy-mmm-dd",
        "dd-mm-yyyy",
        "dd-mmm-yyyy",
        "mm-dd-yyyy",
        "mmm-dd-yyyy",
    ];
    let possibleDateFormats = [...posbleFormatsList];
    for (let dateString of dateStrings) {
        if (
            dateString.includes("/") ||
            dateString.includes("-") ||
            dateString.includes(".")
        ) {
            const seperator = dateString.includes("/")
                ? "/"
                : dateString.includes("-")
                ? "-"
                : dateString.includes(".")
                ? "."
                : "";
            const dateParts = dateString.split(seperator);
            if (dateParts.length === 3) {
                // check if the date is in the format yyyy-mm-dd if not remove it from the list
                if (
                    !isValidYear(dateParts[0]) ||
                    !isValidNumericMonth(dateParts[1]) ||
                    !isValidDay(dateParts[2])
                ) {
                    possibleDateFormats = possibleDateFormats.filter(
                        (format) => format !== "yyyy-mm-dd"
                    );
                }
                // check if the date is in the format yyyy-mmm-dd if not remove it from the list
                if (
                    !isValidYear(dateParts[0]) ||
                    !isValidAbbreviatedMonth(dateParts[1]) ||
                    !isValidDay(dateParts[2])
                ) {
                    possibleDateFormats = possibleDateFormats.filter(
                        (format) => format !== "yyyy-mmm-dd"
                    );
                }
                // check if the date is in the format dd-mm-yyyy if not remove it from the list
                if (
                    !isValidDay(dateParts[0]) ||
                    !isValidNumericMonth(dateParts[1]) ||
                    !isValidYear(dateParts[2])
                ) {
                    possibleDateFormats = possibleDateFormats.filter(
                        (format) => format !== "dd-mm-yyyy"
                    );
                }
                // check if the date is in the format dd-mmm-yyyy if not remove it from the list
                if (
                    !isValidDay(dateParts[0]) ||
                    !isValidAbbreviatedMonth(dateParts[1]) ||
                    !isValidYear(dateParts[2])
                ) {
                    possibleDateFormats = possibleDateFormats.filter(
                        (format) => format !== "dd-mmm-yyyy"
                    );
                }
                // check if the date is in the format mm-dd-yyyy if not remove it from the list
                if (
                    !isValidNumericMonth(dateParts[0]) ||
                    !isValidDay(dateParts[1]) ||
                    !isValidYear(dateParts[2])
                ) {
                    possibleDateFormats = possibleDateFormats.filter(
                        (format) => format !== "mm-dd-yyyy"
                    );
                }
                // check if the date is in the format mmm-dd-yyyy if not remove it from the list
                if (
                    !isValidAbbreviatedMonth(dateParts[0]) ||
                    !isValidDay(dateParts[1]) ||
                    !isValidYear(dateParts[2])
                ) {
                    possibleDateFormats = possibleDateFormats.filter(
                        (format) => format !== "mmm-dd-yyyy"
                    );
                }
            }
        }
    }
    if (possibleDateFormats.length === 0) return posbleFormatsList;
    return possibleDateFormats;
};

/**
 * @description tells if a string is a valid day or not
 * @param {string} dayString
 * @returns {boolean}
 * @example isValidDay("01") // true
 * @example isValidDay("32") // false
 * @example isValidDay("0") // false
 */
export const isValidDay = (dayString: string) => {
    // check if the day is a valid number
    if (isNaN(parseInt(dayString))) return false;
    const day = parseInt(dayString);
    return day > 0 && day <= 31;
};

/**
 * @description tells if a string is a valid numeric month or not
 * @param {string} monthString
 * @returns {boolean}
 * @example isValidNumericMonth("01") // true
 * @example isValidNumericMonth("13") // false
 * @example isValidNumericMonth("0") // false
 */
export const isValidNumericMonth = (monthString: string) => {
    const month = parseInt(monthString);
    return !isNaN(month) && month > 0 && month <= 12;
};

/**
 * @description tells if a string is a valid month abbreviation or not
 * @param {string} monthString
 * @returns {boolean}
 * @example isValidAbbreviatedMonth("Jan") // true
 * @example isValidAbbreviatedMonth("sfdsfd") // false
 */
export const isValidAbbreviatedMonth = (monthString: string) => {
    const monthStrings = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];
    return monthStrings.includes(monthString);
};

/**
 * @description tells if a string is a valid year or not
 * @param {string} yearString
 * @returns {boolean}
 * @example isValidYear("2020") // true
 * @example isValidYear("20200") // false
 * @example isValidYear("202") // false
 */
export const isValidYear = (yearString: string) => {
    // check if the year is a valid number
    if (isNaN(parseInt(yearString))) return false;
    const year = parseInt(yearString);
    return year > 0 && year <= 9999;
};

export const dateFormatMap: { [key: string]: string[] } = {
    "yyyy-mm-dd": [
        "YYYY-M-D",
        "YYYY-M-DD",
        "YYYY-MM-D",
        "YYYY-MM-DD",
        "YYYY/M/D",
        "YYYY/M/DD",
        "YYYY/MM/D",
        "YYYY/MM/DD",
        "YYYY.M.D",
        "YYYY.M.DD",
        "YYYY.MM.D",
        "YYYY.MM.DD",
    ],
    "yyyy-mmm-dd": [
        "YYYY-MMM-D",
        "YYYY-MMM-DD",
        "YYYY/MMM/D",
        "YYYY/MMM/DD",
        "YYYY.MMM.D",
        "YYYY.MMM.DD",
    ],
    "dd-mm-yyyy": [
        "D-M-YYYY",
        "D-MM-YYYY",
        "DD-M-YYYY",
        "DD-MM-YYYY",
        "D/M/YYYY",
        "D/MM/YYYY",
        "DD/M/YYYY",
        "DD/MM/YYYY",
        "D.M.YYYY",
        "D.MM.YYYY",
        "DD.M.YYYY",
        "DD.MM.YYYY",
    ],
    "dd-mmm-yyyy": [
        "D-MMM-YYYY",
        "DD-MMM-YYYY",
        "D/MMM/YYYY",
        "DD/MMM/YYYY",
        "D.MMM.YYYY",
        "DD.MMM.YYYY",
    ],
    "mm-dd-yyyy": [
        "M-D-YYYY",
        "M-DD-YYYY",
        "MM-D-YYYY",
        "MM-DD-YYYY",
        "M/D/YYYY",
        "M/DD/YYYY",
        "MM/D/YYYY",
        "MM/DD/YYYY",
        "M.D.YYYY",
        "M.DD.YYYY",
        "MM.D.YYYY",
        "MM.DD.YYYY",
    ],
    "mmm-dd-yyyy": [
        "MMM-D-YYYY",
        "MMM-DD-YYYY",
        "MMM/D/YYYY",
        "MMM/DD/YYYY",
        "MMM.D.YYYY",
        "MMM.DD.YYYY",
    ],
};
